import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LABELS } from "@match/shared";
import { PageHeader, EmptyState } from "@/components/Layout";
import { AccountPanel, ClientPanel } from "@/components/forms";
import { ClientLedger } from "@/components/ledger";
import { LegActions } from "@/components/pair";
import { Button, COMPUTER_ID_RE, ConnectionState, ErrorNote, Field, inputClass, useCanWrite, useIsAdmin } from "@/components/ui";
import { ArchiveAccount, DeleteForever } from "@/components/delete";
import { api, errorMessage } from "@/lib/api";
import { dateTime, money } from "@/lib/format";
import { useAccountGroups, useClient, useRefreshDirectory } from "@/lib/queries";
import type { Account, ClientDetail as ClientDetailData, ProxyCheck } from "@/lib/types";

export function ClientDetail() {
  const { id = "" } = useParams();
  const canWrite = useCanWrite();
  const client = useClient(id);
  /*
   * LA FASE VIVA DI OGNI CONTO VIENE DA `GET /accounts`.
   *
   * `GET /clients/:id` porta i conti e i cicli, ma non dice QUALE conto sta in
   * quale Fase: i cicli del dettaglio hanno le loro Fasi senza i conti, e
   * dedurlo dall'ordine sarebbe tornare all'abbinamento che questa pagina
   * smette di fare. `/accounts` quel campo ce l'ha per conto
   * (`liveSession`), ed è già in cache arrivando dalla pagina Conti.
   *
   * Se la lettura è ancora in volo la riga semplicemente non mostra la Fase, e
   * compare quando arriva: meglio una riga in meno che una sbagliata.
   */
  const groups = useAccountGroups();
  const gruppo = groups.data?.find((g) => g.client.id === id) ?? null;
  const [editClient, setEditClient] = useState(false);
  const [accountPanel, setAccountPanel] = useState<{ open: boolean; account: Account | null; role: "PROP" | "BROKER" | "NONE" }>({
    open: false,
    account: null,
    role: "PROP",
  });

  if (client.isLoading) return <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>;
  if (client.error || !client.data) {
    return (
      <div className="px-6 py-6">
        <ErrorNote>{client.error ? errorMessage(client.error) : "Cliente non trovato"}</ErrorNote>
        <Link to="/clienti" className="mt-3 inline-block text-sm text-muted hover:text-ink">
          Torna ai clienti
        </Link>
      </div>
    );
  }

  const c = client.data;
  /*
   * DUE ELENCHI, NON UNA COPPIA.
   *
   * Qui c'era «Coppia»: il conto prop e il conto broker più recenti, messi uno
   * accanto all'altro. Faceva vedere un legame che sui conti non esiste — le
   * coppie stanno nelle Fasi, e lo stesso cliente può averne più d'una aperta
   * su conti diversi. Due prop e un broker non erano rappresentabili, e il
   * terzo conto finiva in «Altri conti» come se fosse un avanzo.
   */
  const prop = c.accounts.filter((a) => a.role === "PROP");
  const broker = c.accounts.filter((a) => a.role === "BROKER");
  /*
   * E i conti SENZA RUOLO restano visibili.
   *
   * Non sono previsti dai due elenchi, ma esistono — un conto nasce `NONE`, o
   * ci torna quando lo si toglie dall'operatività — e una pagina che li
   * nasconde fa sparire una riga che c'è davvero. La sezione compare solo
   * quando ce n'è almeno uno.
   */
  const senzaRuolo = c.accounts.filter((a) => a.role === "NONE");

  const addAccount = (role: "PROP" | "BROKER" | "NONE") => setAccountPanel({ open: true, account: null, role });
  const editAccount = (account: Account) => setAccountPanel({ open: true, account, role: account.role });

  /** La Fase viva che tiene un conto, se c'è. Viene da `GET /accounts`. */
  const liveOf = (accountId: string) => gruppo?.accounts.find((a) => a.id === accountId)?.liveSession ?? null;

  const elenco = (titolo: string, conti: Account[], vuoto: string, ruolo: "PROP" | "BROKER" | "NONE") => (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium text-ink">{titolo}</h2>
        {canWrite && (
          <Button size="sm" variant="ghost" onClick={() => addAccount(ruolo)}>
            Aggiungi
          </Button>
        )}
      </div>
      {/* Il proxy è del cliente e vale SOLO sulla gamba prop: senza, quei conti
          non si connettono. Si dice qui una volta, non su ogni riga: il proxy
          è uno per cliente, non uno per conto. */}
      {ruolo === "PROP" && conti.length > 0 && !c.defaultProxy && (
        <p className="mb-2 text-xs text-loss">Nessun proxy sul cliente: i conti prop non si connettono finché non gliene assegni uno.</p>
      )}
      {conti.length === 0 ? (
        <p className="card px-4 py-3 text-sm text-muted">{vuoto}</p>
      ) : (
        <div className="card divide-y divide-line">
          {conti.map((a) => (
            <AccountRow key={a.id} account={a} live={liveOf(a.id)} canWrite={canWrite} onEdit={editAccount} />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <>
      <PageHeader
        title={c.name}
        subtitle={c.email}
        actions={
          canWrite && (
            <>
              <Button onClick={() => setEditClient(true)}>Modifica cliente</Button>
              {/* Il primo ruolo che manca, poi prop: con due elenchi il conto
                  senza ruolo non e' piu' il ripiego di «ne ho gia' due», e il
                  ruolo si cambia comunque nel pannello. */}
              <Button variant="primary" onClick={() => addAccount(prop.length === 0 ? "PROP" : broker.length === 0 ? "BROKER" : "PROP")}>
                Aggiungi conto
              </Button>
            </>
          )
        }
      />

      <div className="space-y-6 px-6 py-5">
        <section className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
          <ComputerIdBox client={c} />
          <ProxyBox client={c} canWrite={canWrite} />
          {c.notes && (
            <div className="max-w-md">
              <p className="text-xs text-faint">Note</p>
              <p className="mt-0.5 text-ink">{c.notes}</p>
            </div>
          )}
        </section>

        <AccessBox client={c} />

        {elenco("Conti prop", prop, "Aggiungi il conto prop: è la gamba di partenza di ogni operazione.", "PROP")}
        {elenco("Conti broker", broker, "Aggiungi il conto broker: riceve la gamba opposta, dimensionata dal moltiplicatore.", "BROKER")}
        {senzaRuolo.length > 0 && elenco("Conti senza ruolo", senzaRuolo, "", "NONE")}

        <ClientLedger client={c} />

        <section>
          <h2 className="mb-2 text-sm font-medium text-ink">Cicli</h2>
          {c.cycles.length === 0 ? (
            <EmptyState
              title="Nessun ciclo"
              hint="Il ciclo parte con la prima fase: collega la coppia, poi crea la fase dalla pagina Fasi."
            />
          ) : (
            /*
             * TUTTI i cicli, e gli ATTIVI si riconoscono.
             *
             * Dal 21/09 gli aperti possono essere più d'uno: ognuno porta la
             * sua etichetta, la sua fase e la sua Fase viva, perché è l'unico
             * modo di sapere a quale challenge appartiene quello che si vede.
             */
            <div className="card divide-y divide-line">
              {c.cycles.map((cy) => {
                const viva = cy.sessions.find((s) => s.state === "ACTIVE" || s.state === "PAUSED") ?? null;
                return (
                  <div key={cy.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${cy.state === "ACTIVE" ? "bg-gain" : "bg-faint"}`} aria-hidden />
                    <span className="sr-only">{cy.state === "ACTIVE" ? "Ciclo attivo" : "Ciclo non attivo"}</span>
                    <span className="font-medium text-ink">{cy.label ?? `${cy.propFirm.name} ${cy.preset.name}`}</span>
                    <span className="text-muted">{LABELS.cyclePhase[cy.currentPhase]}</span>
                    {/* La taglia è nella valuta del conto prop. */}
                    <span className="text-muted">{money(cy.accountSize, prop[0]?.currency ?? "USD")}</span>
                    {viva ? (
                      <Link to={`/fasi/${viva.id}`} className="text-ink hover:underline">
                        {viva.canonicalSymbol} · {LABELS.sessionState[viva.state]}
                      </Link>
                    ) : (
                      <span className="text-faint">Nessuna fase viva</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <ClientPanel open={editClient} client={c} onClose={() => setEditClient(false)} />
      <AccountPanel
        open={accountPanel.open}
        clientId={c.id}
        clientDefaults={{ computerId: c.computerId, defaultProxy: c.defaultProxy }}
        account={accountPanel.account}
        defaultRole={accountPanel.role}
        onClose={() => setAccountPanel((p) => ({ ...p, open: false, account: null }))}
      />
    </>
  );
}

/**
 * Un conto, in una riga.
 *
 * **Nessun abbinamento**: la riga parla di QUESTO conto e basta. Quello che lo
 * lega a qualcosa è la Fase viva, e la Fase la si nomina — «in Fase 1 · ciclo»,
 * col link — invece di farla dedurre dall'accostamento con un altro conto.
 *
 * Le azioni sono quelle di `LegActions`, le stesse della coppia: un secondo
 * elenco di pulsanti qui vorrebbe dire due posti da tenere allineati.
 */
function AccountRow({
  account,
  live,
  canWrite,
  onEdit,
}: {
  account: Account;
  /** La Fase viva che lo tiene, o `null`. */
  live: NonNullable<Account["liveSession"]> | null;
  canWrite: boolean;
  onEdit: (a: Account) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
      <span className="font-medium text-ink">{account.label}</span>
      <span className="machine-id">{account.mt5Login}</span>
      <span className="text-muted">{account.server.displayName ?? account.server.name}</span>
      <span className="italic text-muted">
        {account.lastBalance === null ? "—" : money(account.lastBalance, account.currency)}
        {account.lastEquity !== null && <span className="text-faint"> · equity {money(account.lastEquity, account.currency)}</span>}
      </span>
      <ConnectionState connected={account.isConnected} error={account.lastConnectError} />
      {live && (
        <Link to={`/fasi/${live.id}`} className="text-ink hover:underline">
          in {LABELS.cyclePhase[live.phase]}
          {live.cycleLabel ? ` · ${live.cycleLabel}` : ""}
        </Link>
      )}
      {canWrite && (
        <span className="ml-auto inline-flex flex-wrap items-center gap-2">
          <LegActions account={account} onEdit={onEdit} inline />
          {/* Le due meta' della stessa scelta, e si mostrano insieme:
              archiviare conserva i riferimenti, eliminare li toglie. Un conto
              nato col login sbagliato va ELIMINATO, perche' archiviarlo non
              libererebbe quel numero MT5. */}
          <ArchiveAccount id={account.id} label={account.label} size="sm" />
          <DeleteForever kind="conto" id={account.id} label={account.label} size="sm" />
        </span>
      )}
    </div>
  );
}

/**
 * Il CID del cliente, e la matita per cambiarlo.
 *
 * Uno solo, e non c'e' piu' un «CID del ciclo» da confrontare: `Cycle.computerId`
 * e' una fotografia alla chiusura e non decide niente.
 *
 * **Cambiarlo e' un gesto dichiarato**: la matita apre una conferma che dice il
 * CID di adesso, cosa succede dopo e a chi si vede — non un campo che si salva
 * con gli altri. La matita si vede solo all'ADMIN perche' la rotta e' sua: una
 * maniglia che ad altri risponderebbe 403 e' una maniglia che non deve esserci.
 */
function ComputerIdBox({ client: c }: { client: ClientDetailData }) {
  const isAdmin = useIsAdmin();
  const refresh = useRefreshDirectory();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"GENERATE" | "SET">("GENERATE");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Il maiuscolo lo mette l'interfaccia MENTRE si scrive: il backend rifiuta
  // tutto il resto, e chi scrive vede esattamente quello che manda.
  const scritto = value.trim();
  const invalido = mode === "SET" && scritto !== "" && !COMPUTER_ID_RE.test(scritto);

  const chiudi = () => {
    setOpen(false);
    setMode("GENERATE");
    setValue("");
    setError(null);
  };

  const conferma = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.clients.setComputerId(c.id, mode === "GENERATE" ? { mode: "GENERATE" } : { mode: "SET", value: scritto });
      await refresh();
      chiudi();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="text-xs text-faint">CID</p>
      <p className="mt-0.5 flex items-center gap-2">
        <span className="machine-id">{c.computerId}</span>
        {isAdmin && (
          <button
            type="button"
            onClick={() => (open ? chiudi() : setOpen(true))}
            aria-expanded={open}
            aria-label="Cambia il CID"
            title="Cambia il CID"
            className="text-xs text-muted hover:text-ink"
          >
            ✏
          </button>
        )}
      </p>
      <p className="mt-0.5 text-xs text-faint">Generato alla creazione</p>

      {open && (
        <div className="card mt-2 w-80 max-w-full space-y-2 p-3 text-xs">
          <p className="text-muted">
            CID attuale <span className="machine-id text-ink">{c.computerId}</span>
          </p>

          <div role="radiogroup" aria-label="Come cambiare il CID" className="inline-flex overflow-hidden rounded border border-line">
            {(["GENERATE", "SET"] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                onClick={() => setMode(m)}
                className={`px-3 py-1 ${mode === m ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
              >
                {m === "GENERATE" ? "Genera nuovo" : "Scrivi"}
              </button>
            ))}
          </div>

          {mode === "SET" && (
            <>
              <input
                aria-label="CID nuovo"
                value={value}
                maxLength={32}
                placeholder="32 caratteri esadecimali"
                onChange={(e) => setValue(e.target.value.toUpperCase())}
                className={`${inputClass} machine-id py-1`}
              />
              {invalido && <p className="text-loss">32 caratteri esadecimali, come quello generato.</p>}
            </>
          )}

          {/* Cosa succede davvero, detto prima di farlo: il CID nuovo non si
              vede subito, e dall'altra parte qualcuno puo' accorgersene. */}
          <p className="text-muted">
            Tutti i conti di questo cliente si presenteranno alla prop firm con un dispositivo nuovo dalla prossima connessione.
            Una firm può notare il cambio.
          </p>

          {error && <p className="text-loss">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={chiudi}>
              Annulla
            </Button>
            <Button size="sm" variant="primary" disabled={busy || (mode === "SET" && !COMPUTER_ID_RE.test(scritto))} onClick={() => void conferma()}>
              {busy ? "Cambio…" : "Conferma"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * L'ACCESSO DEL CLIENTE: darlo, spegnerlo, riaccenderlo, rifargli la password.
 *
 * L'utente `CLIENT` si creava **solo** nel form «Nuovo cliente»: per un cliente
 * gia' in anagrafica non c'era modo di darglielo. Qui c'e' la seconda porta, e
 * le tre azioni che servono dopo.
 *
 * **Solo all'ADMIN**, perche' le rotte sono sue: una maniglia che ad altri
 * risponderebbe 403 e' una maniglia che non deve esserci — la stessa regola
 * della matita del CID qui sopra.
 *
 * **La password si vede una volta sola.** Nel database c'e' solo l'hash: non si
 * rilegge, si sostituisce. Quindi il riquadro che la mostra resta aperto finche'
 * non lo si chiude, e non sparisce da solo dopo un rinfresco.
 */
function AccessBox({ client: c }: { client: ClientDetailData }) {
  const isAdmin = useIsAdmin();
  const refresh = useRefreshDirectory();
  const [form, setForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** La password appena generata, mostrata UNA volta. */
  const [mostra, setMostra] = useState<{ email: string; password: string } | null>(null);

  if (!isAdmin) return null;
  const a = c.access;
  // Senza l'email del cliente l'accesso non ha da dove prendere la sua.
  const senzaEmail = c.email.trim() === "";

  const azione = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const crea = () =>
    azione(async () => {
      const creato = await api.clients.createAccess(c.id);
      setMostra({ email: creato.email, password: creato.password });
      setForm(false);
    });

  const reimposta = () =>
    azione(async () => {
      const nuova = await api.users.resetPassword(a!.id);
      setMostra({ email: nuova.email, password: nuova.password });
    });

  return (
    <section>
      <h2 className="mb-2 text-sm font-medium text-ink">Accesso del cliente</h2>
      <div className="card space-y-3 px-4 py-3 text-sm">
        {a ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="machine-id">{a.email}</span>
            <span className={a.isActive ? "text-gain" : "text-loss"}>{a.isActive ? "Attivo" : "Disattivato"}</span>
            {a.mustChangePassword && <span className="text-muted">Deve cambiare la password al primo accesso</span>}
            <span className="ml-auto inline-flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={busy}
                onClick={() =>
                  void azione(async () => {
                    await api.users.setActive(a.id, !a.isActive);
                  })
                }
              >
                {a.isActive ? "Disattiva" : "Riattiva"}
              </Button>
              <Button size="sm" disabled={busy} onClick={() => void reimposta()}>
                Reimposta password
              </Button>
            </span>
          </div>
        ) : form ? (
          <div className="space-y-2">
            <Field label="Email d'accesso">{() => <span className="machine-id">{c.email}</span>}</Field>
            <p className="text-xs text-faint">
              La password la genera il gestionale e <strong>si vede una volta sola</strong>: al primo accesso il cliente
              dovrà cambiarla.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setForm(false); setError(null); }}>
                Annulla
              </Button>
              <Button size="sm" variant="primary" disabled={busy} onClick={() => void crea()}>
                {busy ? "Creo…" : "Crea accesso"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-muted">
              {senzaEmail ? "Serve l'email del cliente per creare un accesso." : "Nessun accesso: questo cliente non entra nell'area riservata."}
            </span>
            <Button size="sm" className="ml-auto" disabled={senzaEmail} onClick={() => setForm(true)}>
              Crea accesso
            </Button>
          </div>
        )}

        {mostra && (
          <div role="status" className="space-y-2 rounded border border-line bg-surface px-3 py-2">
            <p className="text-ink">
              <strong>Si vede solo adesso</strong>: nel database c'è soltanto l'hash, e una password non si rilegge — si
              sostituisce.
            </p>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted">Email</dt>
                <dd className="font-mono text-ink">{mostra.email}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted">Password</dt>
                <dd className="select-all font-mono text-ink">{mostra.password}</dd>
              </div>
            </dl>
            <div className="flex justify-end">
              <Button size="sm" variant="primary" onClick={() => setMostra(null)}>
                Ho copiato la password
              </Button>
            </div>
          </div>
        )}

        {error && <ErrorNote>{error}</ErrorNote>}
      </div>
    </section>
  );
}

/**
 * Il proxy del cliente. Sta qui perche' e' suo: la pagina Proxy non esiste piu'
 * (DECISIONE 19), e con lei se ne sarebbe andato anche il controllo di
 * raggiungibilita' — che e' l'unico modo di sapere che un proxy e' morto prima
 * che lo dica un'apertura fallita.
 */
function ProxyBox({ client: c, canWrite }: { client: ClientDetailData; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState<ProxyCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = c.defaultProxy;

  const controlla = async () => {
    if (!p) return;
    setBusy(true);
    setError(null);
    setCheck(null);
    try {
      setCheck((await api.proxies.check(p.id)).check);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!p) {
    return (
      <div>
        <p className="text-xs text-faint">Proxy</p>
        <p className="mt-0.5 text-warn">Nessuno: i conti prop non si connettono</p>
      </div>
    );
  }

  return (
    <div className="max-w-md">
      <p className="text-xs text-faint">Proxy</p>
      <p className="mt-0.5 text-ink">
        {p.label ? `${p.label} · ` : ""}
        {p.host}:{p.port}
        {[p.city, p.country].filter(Boolean).length ? ` · ${[p.city, p.country].filter(Boolean).join(", ")}` : ""}
      </p>
      <p className="text-xs text-faint">
        {p.type}
        {p.username ? ` · ${p.username}` : ""}
        {p.sessionId ? (
          <>
            {" · sessione "}
            <span className="machine-id">{p.sessionId}</span>
          </>
        ) : null}
      </p>
      <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
        {p.lastCheckAt ? (
          <span className={p.lastCheckOk ? "text-gain" : "text-loss"}>
            {dateTime(p.lastCheckAt)} · {p.lastCheckOk ? "raggiungibile" : "non raggiungibile"}
          </span>
        ) : (
          <span className="text-muted">Mai controllato</span>
        )}
        {canWrite && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void controlla()}>
            {busy ? "Controllo…" : "Controlla"}
          </Button>
        )}
      </p>
      {check && <p className={`text-xs ${check.ok ? "text-gain" : "text-loss"}`}>{check.detail} · {check.elapsedMs} ms</p>}
      {error && <p className="text-xs text-loss">{error}</p>}
    </div>
  );
}
